import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { getCustomOperationTitle } from "../../../../lib/Util";
import { orderColor } from "../../../../lib/Insights";
import type { ITags } from "../../../../lib/Models";
import OrderRectangle from "./OrderRectangle";
import OrderTooltip from "./OrderTooltip";
import MaterialTitle from "./MaterialTitle";
import DraggableOrder from "./DraggableOrder";
import { TypeFilterValues } from "../lineOrderList/filters/TypeFilterConsts";
import { getLineGroupTags } from "../../../../lib/BusinessLogic";
import { LINE_GROUP_TAGS_ACCESS } from "../../../../lib/ManufacturingTags.generated";
import * as r from "../../../../lib/backend/reports.generated.types";
import CollectiveOrderBadge from "./CollectiveOrderBadge";
import { getCursorValue } from "../reducers/properties";

import type { ShiftProps } from "./Shifts";
import type { OwnProps as OrderRectangleProps } from "./OrderRectangle";
import type { MaterialTitleProps } from "./MaterialTitle";
import type { ReduxState, ActionTypes } from "../reducers/reducers";
import type { GanttChartSourceTypes } from "../reducers/properties";
import type { IOrderProducedModelEx } from "../reducers/linesOrders";

type OwnProps = {
    order_uuid: string;
    line_index: number;
    line_uuid: string;
    order_type: string;
    shift_number: number;
    shifts_attributes: ShiftProps[];
    order_rectangle_attributes: OrderRectangleProps;
    material_title_attributes: MaterialTitleProps;
    source_type: GanttChartSourceTypes;
    unscheduled_order: IOrderProducedModelEx;
    order_process_num: string;
    is_linked_operation: boolean;
    earliest_start: number;
};

type Props = OwnProps & ReduxProps;

class UnscheduledOrder extends React.Component<Props> {
    componentDidUpdate(prev_props: Props) {
        if (
            this.props.is_reschedule &&
            this.props.is_reschedule === prev_props.is_reschedule &&
            this.props.earliest_start !== prev_props.earliest_start
        ) {
            const el = document.querySelector(`g[name="order"][order-uuid="${this.props.unscheduled_order.uuid}"]`);
            if (el !== null) {
                setTimeout(() => {
                    el.scrollIntoView({ block: "center", inline: "center" });
                }, 300);
            }
        }
    }
    render() {
        const tags: ITags = this.props.unscheduled_order.tags;
        const { unscheduled_order, material } = this.props;
        const line_group_tags = getLineGroupTags(this.props.line_group_uuid);
        const operation_title_template =
            LINE_GROUP_TAGS_ACCESS.gantt_chart_custom_operation_title_template(line_group_tags);
        const custom_title =
            material !== undefined
                ? getCustomOperationTitle(operation_title_template, unscheduled_order, material)
                : "";
        const unscheduled_align_earliest_end = LINE_GROUP_TAGS_ACCESS.backward_scheduling(line_group_tags);

        const tooltip = (
            <OrderTooltip
                order={null}
                unscheduled_order={this.props.unscheduled_order}
                insight_tooltips={[]}
                properties={[]}
                material={this.props.material}
                tags={tags}
                custom_title={custom_title}
                is_historical={false}
                from_shift_time={this.props.from_shift_time}
                original_earliest_start={this.props.original_earliest_start}
                original_earliest_end={this.props.original_earliest_end}
                original_initial_earliest_start={this.props.original_initial_earliest_start}
                original_initial_earliest_end={this.props.original_initial_earliest_end}
                unscheduled_align_earliest_end={unscheduled_align_earliest_end}
            />
        );

        const cursor = getCursorValue({
            is_ctrl_down: this.props.is_ctrl_down,
            is_rescheduled: this.props.is_reschedule,
            source_type: this.props.source_type,
        });

        return (
            <DraggableOrder
                order_uuid={this.props.order_uuid}
                line_index={this.props.line_index}
                order_index={0}
                order_color={orderColor({
                    order_type: this.props.order_type,
                    has_insight: false,
                })}
                line_uuid={this.props.line_uuid}
                is_unscheduled={true}
                source_type={this.props.source_type}
                x={this.props.order_rectangle_attributes.x}
                y={this.props.order_rectangle_attributes.y}
                width={Math.max(this.props.order_rectangle_attributes.width, this.props.shift_width || 0)}
                height={this.props.order_rectangle_attributes.height}
                is_parallel_line={false}
                opacity={1}
                earliest_start={new Date(this.props.order_rectangle_attributes.earliest_start).getTime()}
                earliest_end={this.props.order_rectangle_attributes.earliest_end}
                order_process_num={this.props.order_process_num}
                production_version={this.props.order_rectangle_attributes.production_version}
                material_external_id={this.props.material_title_attributes.material_external_id}
                is_linked_operation={this.props.is_linked_operation}
            >
                <OrderRectangle tooltip={tooltip} {...this.props.order_rectangle_attributes} cursor={cursor} />
                <CollectiveOrderBadge
                    width={this.props.order_rectangle_attributes.width}
                    height={this.props.order_rectangle_attributes.height}
                    x={this.props.order_rectangle_attributes.x}
                    y={this.props.order_rectangle_attributes.y}
                    order_uuid={this.props.order_uuid}
                    is_unscheduled={true}
                />
                <MaterialTitle
                    key={this.props.material_title_attributes.material_external_id}
                    {...this.props.material_title_attributes}
                    is_rescheduled={this.props.is_reschedule}
                    tooltip={tooltip}
                    is_ctrl_down={this.props.is_ctrl_down}
                    custom_title={custom_title}
                    cursor={cursor}
                />
            </DraggableOrder>
        );
    }
}

const mapState = (state: ReduxState, own_props: OwnProps) => {
    const is_reschedule =
        state.gantt_chart_filters.selected_filter_type === TypeFilterValues.reschedule_operation &&
        state.gantt_chart_filters.reschedule_order_uuid === own_props.order_uuid;
    const planning_table = state.gantt_chart_planning_table;
    const properties = state.gantt_chart_properties;
    const material = state.gantt_chart.materials.find(
        material => material.external_id === own_props.material_title_attributes.material_external_id
    );
    const line_group_uuid = state.gantt_chart_properties.line_group_uuid || "";

    const report_data = state.gantt_chart_report.report_data;
    let from_shift_time: r.ISimulationReportShiftTime | undefined = undefined;
    if (report_data) {
        from_shift_time = report_data.result.from_shift_time;
    }

    let original_earliest_start: number | undefined;
    let original_earliest_end: number | undefined;
    let original_initial_earliest_start: number | undefined;
    let original_initial_earliest_end: number | undefined;

    const original_lines_orders = state.gantt_chart_lines_orders.original_lines_orders || [];
    const original_unscheduled_orders = state.gantt_chart_lines_orders.original_unscheduled_orders || [];

    for (const line of [...original_lines_orders, ...original_unscheduled_orders]) {
        const order = line.orders.find(o => o.uuid === own_props.order_uuid);
        if (order !== undefined) {
            original_earliest_start = order.earliest_start;
            original_earliest_end = order.earliest_end;
            original_initial_earliest_start = order.initial_earliest_start;
            original_initial_earliest_end = order.initial_earliest_end;
            break;
        }
    }

    return {
        is_reschedule,
        is_ctrl_down: planning_table.is_ctrl_down,
        shift_width: properties.shift_width,
        material,
        line_group_uuid,
        from_shift_time,
        original_earliest_start,
        original_earliest_end,
        original_initial_earliest_start,
        original_initial_earliest_end,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(UnscheduledOrder);
